import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { EditProductDialog } from "@/components/EditProductDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { ErrorState, Eyebrow, LoadingState } from "@/components/PageUi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency, formatDate } from "@/lib/format";

type ProductResponse = components["schemas"]["ProductResponse"];

export interface ProductPageProps {
  productId: string;
  /** Called after a successful delete. Falls back to onBack, then history.back(). */
  onDeleted?: () => void;
  /** Called when the user clicks the back affordance. Falls back to history.back() if neither this nor LinkComponent is provided. */
  onBack?: () => void;
  /** Renders the back link as a routed anchor (e.g. next/link, react-router Link). Receives `href="/products"`. */
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  /** Path passed to LinkComponent if provided. Defaults to "/products". */
  backHref?: string;
}

export function ProductPage({
  productId,
  onDeleted,
  onBack,
  LinkComponent,
  backHref = "/products",
}: ProductPageProps) {
  const api = useDolfinClient();
  const [product, setProduct] = useState<ProductResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  async function fetchProduct() {
    if (!productId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await api.GET("/v1/products/{id}", {
        params: { path: { id: productId! } },
      });

      if (apiError || !data) {
        console.error("Failed to load product:", apiError);
        setError("Failed to load product");
        return;
      }

      setProduct(data);
    } catch (e) {
      console.error("Failed to load product:", e);
      setError("Failed to load product");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function handleDelete() {
    if (!productId || deleting) return;
    setConfirmingDelete(false);
    setDeleting(true);

    const { error: apiError } = await api.DELETE("/v1/products/{id}", {
      params: { path: { id: productId } },
    });

    if (apiError) {
      const message = apiErrorMessage(
        apiError,
        "Failed to delete product. It may be referenced by invoices.",
      );
      toast.error(message);
      setDeleting(false);
      return;
    }

    (onDeleted ?? onBack ?? (() => window.history.back()))();
  }

  if (loading && !product) {
    return (
      <PageContainer>
        <LoadingState label="Loading product..." />
      </PageContainer>
    );
  }

  if (error || !product) {
    return (
      <PageContainer>
        <BackAffordance onBack={onBack} LinkComponent={LinkComponent} backHref={backHref} />
        <ErrorState
          message={error ? "Couldn't load this product." : "That product could not be found."}
          onRetry={error ? fetchProduct : undefined}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackAffordance
        onBack={onBack}
        LinkComponent={LinkComponent}
        backHref={backHref}
      />

      <PageHeader
        eyebrow={product.type === "Service" ? "Service" : "Product"}
        title={product.name}
        subtitle={
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">
              {formatCurrency(product.unitPrice, product.currency)}
            </span>
            <span>per unit</span>
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" size="sm" disabled={deleting} onClick={() => setConfirmingDelete(true)}>
              {deleting ? (
                <Spinner className="size-4 mr-1" />
              ) : (
                <Trash2 className="size-4 mr-1" />
              )}
              Delete
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
        <DetailCard title="On an invoice">
          {/* The description is what the customer reads on the line, so it leads. */}
          {product.description ? (
            <p className="text-sm text-pretty">{product.description}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No description. Invoice lines will show the name only.
            </p>
          )}
          <DetailRow label="Type" value={product.type} />
          <DetailRow label="Unit price" value={formatCurrency(product.unitPrice, product.currency)} />
          <DetailRow label="Currency" value={product.currency} />
        </DetailCard>

        <DetailCard title="Record">
          <DetailRow label="Product ID" value={product.id} mono />
          <DetailRow label="Created" value={formatDate(product.createdAt, "long")} />
          <DetailRow label="Last updated" value={formatDate(product.updatedAt, "long")} />
        </DetailCard>
      </div>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {product.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The product will be permanently removed. This fails if any invoice line uses it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditProductDialog
        product={product}
        open={editing}
        onOpenChange={setEditing}
        onUpdated={fetchProduct}
      />
    </PageContainer>
  );
}

function BackAffordance({
  onBack,
  LinkComponent,
  backHref,
}: {
  onBack?: () => void;
  LinkComponent?: ComponentType<{ href: string; children: ReactNode; className?: string }>;
  backHref: string;
}) {
  const className =
    "mb-5 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground";
  const content = (
    <>
      <ArrowLeft className="size-3.5" />
      Back to products
    </>
  );

  if (LinkComponent) {
    return (
      <LinkComponent href={backHref} className={className}>
        {content}
      </LinkComponent>
    );
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => (onBack ? onBack() : window.history.back())}
    >
      {content}
    </button>
  );
}

function DetailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      <Eyebrow className="mb-3">{title}</Eyebrow>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`text-right font-medium ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </span>
    </div>
  );
}
